/* Core & libraries */
import React, {useEffect, useState} from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import moment from "helpers/moment";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

/* Helpers, constants, custom methods, internal libraries */
import { useCompaniesService } from "../../../services/companiesService";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { useUserContext } from "../../../contexts/userContext";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useAnalytics } from "../../../hooks/useAnalytics";
import DeleteActionModal from "../../modals/deleteActionModal";
import { PAGE_TITLES } from "../../../constants/appConstants";
import { useLocaleContext } from "../../../contexts/localeContext.js";

/* Components */
import AddDomainModal from "../../modals/addDomainModal";
import UCModal from "../../ucmodal";
import UCButton from "../../buttons";
import DomainsTabSkeleton from "../skeletonDomainsTab";
import {ReactComponent as MailIconSvg} from "../../../resources/images/mail-icon.svg";
import { ReactComponent as VerticalDotsSvg } from "../../../resources/images/dots-vertical.svg";
import { ReactComponent as DeleteIcon } from "../../../resources/images/delete-icon.svg";
import { ReactComponent as NoDataIcon } from "../../../resources/images/no-data-icon.svg";

/* Styles */
import styles from "./styles.js"

const messages = defineMessages({
  addDomainAriaLabel: {
    id: "settings.domains.add"
  },
})

const EmailDomainsTab = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const { formatMessage } = useIntl();
  const [domains, setDomains] = useState([]);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [showDeleteDomainModal, setShowDeleteDomainModal] = useState(false);
  const [showAddDomainModal, setShowAddDomainModal] = useState(false);
  const [showAddDomainSuccessModal, setShowAddDomainSuccessModal] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(null)

  const globalLocale = useLocaleContext();
  const { setNotification } = useNotificationContext();
  const { getDomains, resendVerification, removeDomain, addDomain } = useCompaniesService();
  const userDetails = useUserContext();
  const { checkPermission } = useHasPermission();
  const { initAnalytics } = useAnalytics();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["domains"]
    };
    initAnalytics(initUtagData, 'init call', 'view')
    getDomainList()
  }, [])

  const getDomainList = async () =>  {
    setShowSkeleton(true)
    const response = await getDomains(companyEntityId);
    if (response) {
      setDomains(response?.domainList);
    }
    setShowSkeleton(false)
  }

  const handleDelete = item => {
    document.getElementById("root").click();
    setCurrentDomain(item)
    setShowDeleteDomainModal(true)
  }

  const deleteAction = async () =>  {
    const response = await removeDomain({companyEntityId, domainId: currentDomain.domainId, domain: currentDomain.domain})
    if (response) {
      getDomainList()
      setShowDeleteDomainModal(false)
    }
  }
  const handleAdd = async (values) => {
    const firstRegEmailDomain = userDetails.userInfo.emailAddress.split("@")
    const matchFirstRegisteredDomain = (values.domain).toLowerCase() === (firstRegEmailDomain[1]).toLowerCase();

    const response = await addDomain({...values, companyEntityId, locale: globalLocale});
    if (response) {
      setCurrentDomain(values)
      setShowAddDomainModal(false)
      if (matchFirstRegisteredDomain) {
        getDomainList();
      } else {
        setShowAddDomainSuccessModal(true)
      }
    }

    //when an ATM is adding the same domain as the first ATM registering - we are not sending an email or making them verify the domain so send success msg to confirm
    if (response && matchFirstRegisteredDomain) {
      setNotification(
        "success",
        <FormattedMessage id="settings.domains.firstDomain.title" />,
        <FormattedMessage id="settings.domains.firstDomain.description" />,
        false,
        10000
      )
    }
  }

  const renderPopover = (item) => {
    return(
      <Popover className={classes.popoverStyle}>
        <div  className={classes.popoverDiv}>
          {
            item.status === 2 &&
            <div
              className={classes.popoverAction}
              onClick={() => {
                resendVerification({
                  companyEntityId,
                  domainId: item.domainId,
                });
                return document.getElementById("root").click();
              }}
            >
              <FormattedMessage id={`settings.domains.resend`} />
            </div>
          }
          {
            checkPermission("F_COMPANYSETTINGS_DELETEDOMAIN") &&
              <div className={classes.popoverActionDelete} onClick={() => handleDelete(item)}>
                <FormattedMessage id={`settings.domains.delete`} />
              </div>
          }
        </div>

      </Popover>
    )
  }

  const renderNoDomains = () => {
    return(
      <div  className={classes.noData}>
        <NoDataIcon className={classes.iconColor}/>
        <p className={classes.noDataTitle}><FormattedMessage id={"settings.domains.noDataTitle"} /></p>
        <p className={classes.noDataDescription}><FormattedMessage id={"settings.domains.noDataDescription"} /></p>
        {checkPermission("F_COMPANYSETTING_EDITDOMAIN") && (
          <UCButton
            type="primary"
            className={classes.noDataButton}
            onClick={() => setShowAddDomainModal(true)}
            text={<FormattedMessage id="settings.domains.addFirst"/>}
          />
        )}
      </div>
    )
  }

  const handleLeaveClick = (keyValue) => {
    if (keyValue === overlayOpen) {
      setOverlayOpen(null)
    }
  };

  const handleEnterClick = (key) => {
    setOverlayOpen(key)
  };

  return (
    <>
      <div className={classes.details}>
        <div className={classes.txtDiv}>
          <div className={classes.title}>
            <FormattedMessage id="settings.domains.title"/>
          </div>
          <div className={classes.description}>
            <FormattedMessage id="settings.domains.description"/>
          </div>
        </div>
        {checkPermission("F_COMPANYSETTING_EDITDOMAIN") && (
          <div className={classes.btnContainer}>
            {!showSkeleton && (
              <UCButton
                type="primary"
                className={classes.button}
                onClick={() => setShowAddDomainModal(true)}
                text={<FormattedMessage id="settings.domains.add"/>}
                tabIndex={0}
                ariaLabel={formatMessage(messages.addDomainAriaLabel)}
              />
            )}
          </div>
        )}
      </div>
      {showSkeleton ? (<DomainsTabSkeleton />) : (
        <div className={classes.emailDomainsTab}>
          <div className={classes.domainsWrapper}>
            {
              domains.length ?
                domains.map((item, key) => (
                  <div key={item.domainId} className={classes.row}>
                    <div>
                      <p className={classes.domainName}>
                        { item.domain }
                        {
                          item.status === 2 &&
                          <span className={classes.unverified}><FormattedMessage id={"settings.domains.unverified"} /></span>
                        }
                      </p>
                      <p className={classes.createdOn}>
                        <FormattedMessage id={"settings.domains.added"} />
                        <span>{ moment(item.createdOn).locale(globalLocale).format("MMM DD, YYYY") }</span>
                      </p>
                    </div>
                    {checkPermission("F_COMPANYSETTING_EDITDOMAIN") && 
                      <div className={classes.dotsButtonContainer}>
                        <OverlayTrigger
                          trigger="click"
                          rootClose
                          placement="bottom"
                          overlay={renderPopover(item)}
                          onEnter={() => handleEnterClick(key)}
                          onExit={() => handleLeaveClick(key)}
                        >
                          <div className={`${classes.dotsStyle} ${overlayOpen === key  ? classes.dotsOpenStyle : ""}`}>
                            <VerticalDotsSvg />
                          </div>
                        </OverlayTrigger>
                      </div>
                    }     
                  </div>
                  )
                )
              :
                renderNoDomains()
            }
          </div>

          <DeleteActionModal
            showModal={showDeleteDomainModal}
            setShowModal={setShowDeleteDomainModal}
            modalIcon={ <DeleteIcon /> }
            modalTitle={ <FormattedMessage id="settings.domains.deleteAction.title" values={{domain: `${currentDomain?.domain }`}}/> }
            modalDescription={ <FormattedMessage id="settings.domains.deleteAction.description" values={{domain: `${currentDomain?.domain }`}} /> }
            submitCallback={deleteAction}
            setSelectedRow={(value) => {}}
          />
          <AddDomainModal
            showModal={showAddDomainModal}
            setShowModal={setShowAddDomainModal}
            handleAdd={handleAdd}
          />

          <UCModal
            onExited= {getDomainList}
            className={classes.confirmationModal}
            showModal={ showAddDomainSuccessModal }
            setShowModal={ setShowAddDomainSuccessModal }
            modalIcon={ <MailIconSvg className={classes.iconColor} /> }
            modalTitle={ <FormattedMessage id="settings.domains.add.successfullyTitle"/> }
            modalContent={
              <div>
                <FormattedMessage id={"settings.domains.add.successfullyNote"} values={{domain: currentDomain?.emailAddress}} />
                <UCButton
                  onClick={() => setShowAddDomainSuccessModal(false)}
                  type="primary"
                  className={classes.cancelButton}
                  text={<FormattedMessage id="settings.domains.add.close"/>}
                />
              </div>
            }
            dataTestId="addDomainSuccessfullyModal"
          />
        </div>
      )}
    </>
  )
}

export default EmailDomainsTab;
